import { usePrenotaStore } from "@PN/inclusao";
import { create } from "zustand";

export interface UploadAnexo {
  seq: string;
  doc: string;
  file: File;
  tipo?: string;
}

interface UploadAnexoStore {
  uploads: UploadAnexo[];
  addUpload: (upload: UploadAnexo) => void;
  removeUpload: (seq: string) => void;
  clearUploads: () => void;
  removeDuplicates: () => void;
}

export const useUploadAnexoStore = create<UploadAnexoStore>((set) => ({
  uploads: [],
  addUpload: (upload) =>
    set((state) => ({
      uploads: [...state.uploads.filter((u) => u.seq !== upload.seq), upload],
    })),
  removeUpload: (seq) =>
    set((state) => ({
      uploads: state.uploads.filter((u) => u.seq !== seq),
    })),
  clearUploads: () => set({ uploads: [] }),
  removeDuplicates: () =>
    set((state) => {
      const seen = new Set<string>();
      const uniqueUploads = state.uploads.filter((upload) => {
        if (seen.has(upload.seq)) {
          return false;
        }
        seen.add(upload.seq);
        return true;
      });
      return { uploads: uniqueUploads };
    }),
}));

/**
 * Centraliza o acesso e mapeamento dos uploads de anexos
 */
export const anexosUploadState = {
  /**
   * Mapeia item sequencial (ex: 001, 002) para o ID automático do upload
   * @param item string
   * @param idMapping Map<string, string>
   */
  getAutoIdByItem(
    item: string,
    idMapping: Map<string, string>
  ): string | undefined {
    return idMapping.get(item);
  },

  /**
   * Mapeia ID automático do upload para o item sequencial
   * @param autoId string
   * @param idMapping Map<string, string>
   */
  getItemByAutoId(
    autoId: string,
    idMapping: Map<string, string>
  ): string | undefined {
    for (const [item, id] of idMapping.entries()) {
      if (id === autoId) return item;
    }
    return undefined;
  },

  /**
   * Obtém o objeto File a partir do item sequencial
   * @param item string
   * @param idMapping Map<string, string>
   */
  getFileByItem(item: string, idMapping: Map<string, string>) {
    const autoId = idMapping.get(item);
    if (!autoId) return undefined;
    const uploadFiles = usePrenotaStore.getState().aux.uploadFiles || new Map();
    return uploadFiles.get(autoId)?.file;
  },

  /**
   * Obtém o objeto File a partir do ID automático
   * @param autoId string
   */
  getFileByAutoId(autoId: string) {
    const uploadFiles = usePrenotaStore.getState().aux.uploadFiles || new Map();
    return uploadFiles.get(autoId)?.file;
  },

  /**
   * Limpa todos os uploads
   */
  clearUploads() {
    usePrenotaStore.getState().actions.aux.files.clear();
  },
};
